<template>
	<div class="container" id="faq-page" style="max-width: 780px">

		<div class="page-header gold mt-4 mb-2">
			<back-btn to="/dashboard"></back-btn>
			<h2 class="mb-0">
				Help
			</h2>
		</div>

    <div class="mb-5 text-center mx-auto" style="max-width: 780px; font-size: 15px; color: #666;">
      <p class="mb-1">
        Our community is important to us, and we take your experience seriously.
      </p>
      <p class="mb-1">
        Please review the Frequently Asked Questions below first, as these may help answer your question quickly.
      </p>
      <p>
        If you require further support, have ideas for improving our community, or have experienced something that you feel should be addressed, please contact us using the form below.
      </p>
    </div>

    <h3 class="gold text-center mb-3">Frequently Asked Questions</h3>

		<!-- Loading -->
		<div v-if="loading.faqs" class="text-center py-5">
			<b-spinner style="color: #d6b674;"></b-spinner>
			<p class="mt-2 text-muted small">Loading FAQs…</p>
		</div>

		<!-- Empty -->
		<div v-else-if="!faqs.length" class="faq-empty">
			<i class="fas fa-question-circle faq-empty__icon"></i>
			<p class="faq-empty__text">No FAQs available yet.</p>
		</div>

		<!-- FAQ accordion -->
		<div v-else class="faq-list">
			<div
				v-for="faq in faqs"
				:key="faq.id"
				class="faq-item"
				:class="{ 'faq-item--open': openId === faq.id }"
			>
				<button class="faq-item__trigger" @click="toggle(faq.id)" :aria-expanded="openId === faq.id ? 'true' : 'false'">
					<span class="faq-item__question">{{ faq.question }}</span>
					<span class="faq-item__chevron">
						<i class="fas fa-chevron-down"></i>
					</span>
				</button>

				<transition name="faq-slide">
					<div v-if="openId === faq.id" class="faq-item__body">
						<div class="faq-item__answer" v-html="faq.answer"></div>
					</div>
				</transition>
			</div>
		</div>

    <div class="mx-auto mb-4" style="max-width: 700px">
      <h2 class="gold text-center mb-2">Contact Us</h2>

      <form @submit.prevent="submitMessage()">
        <div class="form-group">
          <input v-model="form.name" required type="text" class="form-control" placeholder="Name">
        </div>
        <div class="form-group">
          <input v-model="form.email" required type="email" class="form-control" placeholder="Enter email">
        </div>
        <div class="form-group">
          <textarea v-model="form.message" required class="form-control" placeholder="Message" rows="3"></textarea>
        </div>
        <button type="submit" class="btn btn-primary" :disabled="loading.message">Submit</button>
        <small v-if="message" class="d-block text-success"><i>{{ message }}</i></small>
      </form>
    </div>

	</div>
</template>

<script>
import axios from '@/axios';
import BackBtn from '@/components/BackBtn.vue';

export default {
	name: 'FaqPage',
	components: { BackBtn },
	data() {
		return {
			faqs: [],
			loading: {
        faqs: true,
        message: false
      },
			openId: null,
      form: {},
      message: ''
		};
	},
	methods: {
		toggle(id) {
			this.openId = this.openId === id ? null : id;
		},
		fetchFaqs() {
			axios({ url: 'faqs' })
				.then(response => {
					this.loading.faqs = false;
					this.faqs = response.data;
				})
				.catch(() => {
					this.loading.faqs = false;
					this.faqs = [];
				});
		},
    async submitMessage() {
      this.loading.message = true;
      await axios.post('faqs/contact', this.form)
      this.loading.message = false;
      this.message = 'Your message has been submitted.';
      this.form.message = '';
    }
	},
	mounted() {
		this.fetchFaqs();

    const profile = this.$store.state.profile;
    this.form.name = `${profile.first_name} ${profile.last_name}`;
    this.form.email = profile.email
  },
};
</script>

<style scoped>
/* ── Page header ───────────────────────────────── */
.page-header {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 14px;
	position: relative;
	padding: 0 52px;
}
.page-header h2 {
	min-width: 0;
	text-align: center;
	word-break: break-word;
}
.gold { color: #d6b674; }

/* ── Empty state ────────────────────────────────── */
.faq-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 60px 16px;
	color: #bbb;
}
.faq-empty__icon {
	font-size: 3.5rem;
	color: #d6b674;
	opacity: 0.5;
	margin-bottom: 16px;
}
.faq-empty__text { font-size: 1rem; }

/* ── Accordion list ─────────────────────────────── */
.faq-list {
	max-width: 780px;
	margin: 0 auto 48px;
	display: flex;
	flex-direction: column;
	gap: 10px;
}

/* ── Each item ──────────────────────────────────── */
.faq-item {
	border: 1px solid #e8dcc6;
	border-radius: 10px;
	overflow: hidden;
	background: #fff;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
	transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.faq-item--open {
	border-color: #d6b674;
	box-shadow: 0 4px 14px rgba(214, 182, 116, 0.18);
}

/* ── Trigger button ─────────────────────────────── */
.faq-item__trigger {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 18px 22px;
	background: none;
	border: none;
	text-align: left;
	cursor: pointer;
	outline: none;
}
.faq-item__trigger:focus-visible {
	outline: 2px solid #d6b674;
	outline-offset: -2px;
}
.faq-item__question {
	font-size: 1rem;
	font-weight: 600;
	color: #2d2d2d;
	line-height: 1.4;
	flex: 1;
}
.faq-item--open .faq-item__question {
	color: #b8963e;
}

/* ── Chevron ────────────────────────────────────── */
.faq-item__chevron {
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: #f5efe1;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #d6b674;
	font-size: 0.75rem;
	transition: transform 0.25s ease, background 0.2s ease;
}
.faq-item--open .faq-item__chevron {
	transform: rotate(180deg);
	background: #d6b674;
	color: #fff;
}

/* ── Answer body ────────────────────────────────── */
.faq-item__body {
	border-top: 1px solid #f0e8d4;
	background: #fffdf8;
}
.faq-item__answer {
	padding: 18px 22px 22px;
	font-size: 0.95rem;
	color: #444;
	line-height: 1.7;
}

/* ── Rich-text answer reset ─────────────────────── */
.faq-item__answer ::v-deep p { margin-bottom: 0.6em; }
.faq-item__answer ::v-deep p:last-child { margin-bottom: 0; }
.faq-item__answer ::v-deep ul,
.faq-item__answer ::v-deep ol { padding-left: 1.4em; margin-bottom: 0.6em; }
.faq-item__answer ::v-deep a { color: #b8963e; text-decoration: underline; }
.faq-item__answer ::v-deep h2,
.faq-item__answer ::v-deep h3 { font-size: 1em; font-weight: 700; margin-bottom: 0.4em; color: #2d2d2d; }
.faq-item__answer ::v-deep blockquote {
	border-left: 3px solid #d6b674;
	padding-left: 12px;
	color: #666;
	margin: 0.6em 0;
	font-style: italic;
}

/* ── Slide transition ───────────────────────────── */
.faq-slide-enter-active,
.faq-slide-leave-active {
	transition: max-height 0.28s ease, opacity 0.2s ease;
	overflow: hidden;
	max-height: 600px;
}
.faq-slide-enter,
.faq-slide-leave-to {
	max-height: 0;
	opacity: 0;
}

/* ── Mobile ─────────────────────────────────────── */
@media (max-width: 576px) {
	.faq-item__trigger { padding: 14px 16px; }
	.faq-item__answer { padding: 14px 16px 18px; }
	.faq-item__question { font-size: 0.95rem; }
}
</style>
